import React, { useCallback, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    CountByPrimaryFilter,
    EmployeeData,
    MonitorViewFleetSummaryTotals,
    VehicleData,
} from "../../../models/monitorView/monitorViewTypes";
import VehicleFilters from "./filters/vehicleFilters";
import VehicleGroupFilter from "./filters/vehicleGroupFilter";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import SortIcon from "@mui/icons-material/Sort";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    setSelectedVehicleGroupIds,
    setSortOrder,
    SortOrder,
} from "../../../store/monitorViewFiltersSlice";
import { MonitorViewWorktimeFilters } from "./filters/monitorViewWorktimeFilters";
import { handleVehicleSelect } from "../monitorViewUtils";
import {
    setSelectedEmployeeId,
    toggleWorkShiftView,
} from "../../../store/monitorViewSlice";
import { getEmployeeGroups } from "../../../services/employeeGroupService";
import { useFetch } from "../../../hooks/useFetch";
import { employeeGroupListItemDto } from "../../../models/employeeGroup/employeeGroup";
import FleetOverviewCard from "./fleetOverviewCard/fleetOverviewCard";

interface MonitorViewOverviewSidebarProps {
    vehicleData: VehicleData[];
    employeeData: EmployeeData[];
    countByPrimaryFilter: CountByPrimaryFilter;
    fleetSummaryTotals: MonitorViewFleetSummaryTotals;
    countByWorkTimeType?: { [key: string]: number };
    countByDrivingStatus?: { [key: number]: number };
}

export const MonitorViewOverviewSidebar = ({
    vehicleData,
    employeeData,
    countByPrimaryFilter,
    fleetSummaryTotals,
    countByWorkTimeType,
    countByDrivingStatus,
}: MonitorViewOverviewSidebarProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedVehicleGroupIds = useAppSelector(
        (state) => state.monitorViewFilters.selectedVehicleGroupIds
    );
    const sortOrder = useAppSelector(
        (state) => state.monitorViewFilters.sortOrder
    );
    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );
    const isVehicleDetailView = useAppSelector(
        (state) => state.monitorView.isVehicleDetailView
    );
    const selectedEmployeeId = useAppSelector(
        (state) => state.monitorView.selectedEmployeeId
    );
    const [employeeGroups, setEmployeeGroups] =
        useState<employeeGroupListItemDto[]>();
    const [searchText, setSearchText] = useState("");
    const vehicleCameras = useAppSelector(
        (state) => state.vehicle.vehicleCameras
    );

    // Build a Map from vehicleId -> camera data for O(1) lookups (avoids per-card Redux subscription + .find())
    const vehicleCameraByVehicleId = useMemo(() => {
        const map = new Map<string, (typeof vehicleCameras)[number]>();
        for (const c of vehicleCameras ?? []) {
            if (c.vehicleId) {
                map.set(c.vehicleId, c);
            }
        }
        return map;
    }, [vehicleCameras]);

    const onSelectVehicleId = useCallback(
        (vehicleId: string) => {
            handleVehicleSelect(
                vehicleId,
                vehicleData,
                isVehicleDetailView,
                dispatch
            );
        },
        [vehicleData, isVehicleDetailView, dispatch]
    );

    const onSelectEmployeeId = useCallback(
        (employeeId: string) => {
            dispatch(setSelectedEmployeeId(employeeId));
            dispatch(toggleWorkShiftView());
        },
        [dispatch]
    );

    useFetch(
        (signal) => getEmployeeGroups(true, signal),
        {
            onSuccess: (res) =>
                setEmployeeGroups(
                    res.data.filter((d) => !!d.salaryPeriodTypeId)
                ),
        },
        []
    );

    const employeesWithoutVehicles = useMemo(() => {
        const assignedEmployeeIds = new Set(
            vehicleData?.map((v) => v.employeeId?.toLowerCase())
        );
        return employeeData?.filter(
            (employee) =>
                !assignedEmployeeIds.has(employee.employeeId?.toLowerCase())
        );
    }, [employeeData, vehicleData]);

    // Build a Map from employeeId (lowercase) -> EmployeeData for O(1) lookups
    const employeeByIdMap = useMemo(() => {
        const map = new Map<string, EmployeeData>();
        for (const e of employeeData ?? []) {
            if (e.employeeId) {
                map.set(e.employeeId.toLowerCase(), e);
            }
        }
        return map;
    }, [employeeData]);

    const findEmployeeForVehicle = useCallback(
        (vehicle: VehicleData): EmployeeData | undefined => {
            if (!vehicle.employeeId) return undefined;
            return employeeByIdMap.get(vehicle.employeeId.toLowerCase());
        },
        [employeeByIdMap]
    );

    const matchesSearch = useCallback(
        (vehicle: VehicleData | null, employee: EmployeeData | null) => {
            if (!searchText.trim()) return true;
            const term = searchText.toLowerCase().trim();
            const fields: (string | null | undefined)[] = [
                vehicle?.registerNumber,
                vehicle?.brand,
                employee?.employeeName,
                employee?.workTimeTypeName,
            ];
            return fields.some((f) => f?.toLowerCase().includes(term));
        },
        [searchText]
    );

    const filteredVehicles = useMemo(
        () =>
            vehicleData?.filter((v) =>
                matchesSearch(v, findEmployeeForVehicle(v))
            ) ?? [],
        [vehicleData, matchesSearch, findEmployeeForVehicle]
    );

    const filteredEmployeesWithoutVehicles = useMemo(
        () =>
            employeesWithoutVehicles?.filter((e) => matchesSearch(null, e)) ??
            [],
        [employeesWithoutVehicles, matchesSearch]
    );

    type CardEntry =
        | { type: "vehicle"; vehicle: VehicleData; employee?: EmployeeData }
        | { type: "employeeOnly"; employee: EmployeeData };

    const sortedCards = useMemo(() => {
        const entries: CardEntry[] = [
            ...filteredVehicles.map((v) => ({
                type: "vehicle" as const,
                vehicle: v,
                employee: findEmployeeForVehicle(v),
            })),
            ...filteredEmployeesWithoutVehicles.map((e) => ({
                type: "employeeOnly" as const,
                employee: e,
            })),
        ];

        return entries.sort((a, b) => {
            const empNameA =
                a.type === "vehicle"
                    ? (a.employee?.employeeName ?? "")
                    : (a.employee.employeeName ?? "");
            const empNameB =
                b.type === "vehicle"
                    ? (b.employee?.employeeName ?? "")
                    : (b.employee.employeeName ?? "");
            const regA =
                a.type === "vehicle" ? (a.vehicle.registerNumber ?? "") : "";
            const regB =
                b.type === "vehicle" ? (b.vehicle.registerNumber ?? "") : "";

            if (sortOrder === "registerNumber") {
                // Employees without vehicles go to the bottom
                if (!regA && regB) return 1;
                if (regA && !regB) return -1;
                return regA.localeCompare(regB);
            }
            // Sort by employee name; vehicles without employee at the bottom
            const hasEmpA = !!empNameA;
            const hasEmpB = !!empNameB;
            if (!hasEmpA && hasEmpB) return 1;
            if (hasEmpA && !hasEmpB) return -1;
            return empNameA.localeCompare(empNameB);
        });
    }, [
        filteredVehicles,
        filteredEmployeesWithoutVehicles,
        findEmployeeForVehicle,
        sortOrder,
    ]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: sortedCards.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => 120,
        overscan: 5,
    });

    const renderCard = useCallback(
        (entry: CardEntry) => {
            if (entry.type === "vehicle") {
                return (
                    <FleetOverviewCard
                        vehicle={entry.vehicle}
                        employee={entry.employee}
                        employeeGroups={employeeGroups}
                        isSelected={
                            entry.vehicle.vehicleId === selectedVehicleId
                        }
                        cameraData={vehicleCameraByVehicleId.get(entry.vehicle.vehicleId)}
                        onSelectVehicle={onSelectVehicleId}
                        onSelectEmployee={onSelectEmployeeId}
                    />
                );
            }
            return (
                <FleetOverviewCard
                    vehicle={null}
                    employee={entry.employee}
                    employeeGroups={employeeGroups}
                    isSelected={
                        entry.employee.employeeId === selectedEmployeeId
                    }
                    onSelectVehicle={onSelectVehicleId}
                    onSelectEmployee={onSelectEmployeeId}
                />
            );
        },
        [
            employeeGroups,
            selectedVehicleId,
            selectedEmployeeId,
            vehicleCameraByVehicleId,
            onSelectVehicleId,
            onSelectEmployeeId,
        ]
    );

    const hasCards =
        filteredVehicles?.length > 0 ||
        filteredEmployeesWithoutVehicles?.length > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            <Grid container>
                <Grid item xs={12}>
                    <VehicleFilters
                        countByPrimaryFilter={countByPrimaryFilter}
                    />
                </Grid>
                <Grid item xs={12}>
                    <MonitorViewWorktimeFilters
                        countByWorkTimeType={countByWorkTimeType}
                        countByDrivingStatus={countByDrivingStatus}
                    />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <VehicleGroupFilter
                        selectedGroupIds={selectedVehicleGroupIds}
                        setSelectedVehicleGroupIds={setSelectedVehicleGroupIds}
                    />
                </Grid>
                <Grid item xs={12} sx={{ px: 1, py: 0.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={t(
                            "monitorView.sidebar.overview.header.searchPlaceholder"
                        )}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 1, py: 0.5 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {t(
                            "monitorView.sidebar.overview.header.activeVehicleCountSummary",
                            { vehicles: fleetSummaryTotals.activeVehicles }
                        )}
                        {" / "}
                        {t(
                            "monitorView.sidebar.overview.header.activeEmployeeCountSummary",
                            { employees: fleetSummaryTotals.activeEmployees }
                        )}
                    </Typography>
                    <ToggleButtonGroup
                        value={sortOrder}
                        exclusive
                        onChange={(_, val: SortOrder) => {
                            if (val) dispatch(setSortOrder(val));
                        }}
                        size="small"
                    >
                        <ToggleButton
                            value="employeeName"
                            title={t("monitorView.sort.byEmployee")}
                            sx={{ px: 1, gap: 0.5 }}
                        >
                            <SortIcon sx={{ fontSize: 14 }} />
                            <PersonIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="registerNumber"
                            title={t("monitorView.sort.byVehicle")}
                            sx={{ px: 1, gap: 0.5 }}
                        >
                            <SortIcon sx={{ fontSize: 14 }} />
                            <DirectionsCarIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            {hasCards ? (
                <Box
                    ref={scrollContainerRef}
                    sx={{ overflowY: "auto", flex: 1 }}
                >
                    <div
                        style={{
                            height: virtualizer.getTotalSize(),
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                            const entry = sortedCards[virtualItem.index];
                            const key =
                                entry.type === "vehicle"
                                    ? `vehicle-${entry.vehicle.vehicleId}-employee-${entry.vehicle.employeeId ?? "none"}`
                                    : `vehicle-novehicle-employee-${entry.employee.employeeId}`;
                            return (
                                <div
                                    key={key}
                                    data-index={virtualItem.index}
                                    ref={virtualizer.measureElement}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                >
                                    {renderCard(entry)}
                                </div>
                            );
                        })}
                    </div>
                </Box>
            ) : (
                <Alert variant="standard" severity="info">
                    {t("monitorView.sidebar.overview.noRows")}
                </Alert>
            )}
        </Box>
    );
};
